import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Alert, Card } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext'; // Importar para feedback

const TagsInteresseTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal(); // Usar o modal de alerta global
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Loading para o botão de salvar do modal
  const [currentTag, setCurrentTag] = useState(null); // Para edição
  const [tagData, setTagData] = useState({ nome: '', descricao: '' });

  const fetchTags = useCallback(async () => { // Usar useCallback
    if (!currentEntidade) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/interesses?entidade_id=${currentEntidade.id}`);
      setTags(response.data.interesses || []);
    } catch (err) {
      console.error("Erro ao buscar tags:", err);
      setError('Não foi possível carregar as tags de interesse.');
      setTags([]); // Garante que tags seja um array vazio em caso de erro
    } finally {
      setIsLoading(false);
    }
  }, [currentEntidade]); // Dependência do useCallback

  useEffect(() => {
    fetchTags();
  }, [fetchTags]); // useEffect agora depende da função memoizada

  const handleOpenModal = (tag = null) => {
    setCurrentTag(tag);
    setTagData(tag ? { nome: tag.nome, descricao: tag.descricao || '' } : { nome: '', descricao: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentTag(null);
    setTagData({ nome: '', descricao: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTagData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveTag = async () => {
    if (!tagData.nome.trim()) {
      showAlertModal('Atenção', 'O nome da tag não pode ser vazio.', 'warning');
      return;
    }
    if (!currentEntidade) {
      showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger');
      return;
    }

    setIsSaving(true);
    try {
      if (currentTag && currentTag.id) {
        // Atualizando uma tag existente
        const response = await axios.put(`/api/interesses/${currentTag.id}`, tagData);
        const updatedTag = response.data.interesse;
        setTags(prevTags => prevTags.map(tag => tag.id === updatedTag.id ? updatedTag : tag));
        showAlertModal('Sucesso', 'Tag atualizada com sucesso!', 'success');
      } else {
        // Criando uma nova tag
        const response = await axios.post('/api/interesses', {
          ...tagData,
          entidade_id: currentEntidade.id,
        });
        const newTag = response.data.interesse;
        setTags(prevTags => [...prevTags, newTag].sort((a, b) => a.nome.localeCompare(b.nome)));
        showAlertModal('Sucesso', 'Tag criada com sucesso!', 'success');
      }
      handleCloseModal();
      // Não é mais necessário re-buscar, a lista é atualizada localmente.
    } catch (error) {
      console.error("Erro ao salvar tag:", error);
      showAlertModal('Erro', `Falha ao salvar a tag: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm('Tem certeza que deseja excluir esta tag? A exclusão também removerá o vínculo desta tag de todos os pacientes.')) {
      try {
        await axios.delete(`/api/interesses/${tagId}`);
        setTags(prevTags => prevTags.filter(tag => tag.id !== tagId)); // Remove a tag da lista localmente
        showAlertModal('Sucesso', 'Tag excluída com sucesso!', 'success');
      } catch (error) {
        console.error("Erro ao excluir tag:", error);
        showAlertModal('Erro', `Falha ao excluir a tag: ${error.response?.data?.message || error.message}`, 'danger');
      }
    }
  };

  return (
    <div>
      <Card className="mb-3 bg-light border-0 shadow-sm">
        <Card.Body>
          <Card.Title as="h6">Para que servem as Tags de Interesse?</Card.Title>
          <Card.Text className="small text-muted">
            As tags de interesse ajudam a segmentar seus pacientes para ações de marketing e comunicação.
            Por exemplo, você pode criar uma tag "Botox" e vinculá-la a todos os pacientes que já realizaram ou têm interesse neste procedimento.
            Futuramente, você poderá enviar uma promoção específica para todos os pacientes com esta tag.
          </Card.Text>
        </Card.Body>
      </Card>
      {error && <Alert variant="danger">{error}</Alert>}
      <Button variant="primary" onClick={() => handleOpenModal()} className="mb-3">
        Adicionar Nova Tag
      </Button>

      {isLoading && !showModal ? ( // Mostra o spinner principal apenas se o modal não estiver aberto
        <div className="text-center"><Spinner animation="border" /></div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Nome da Tag</th>
              <th>Descrição</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {tags.map(tag => (
              <tr key={tag.id}>
                <td>{tag.id}</td>
                <td>{tag.nome}</td>
                <td>{tag.descricao}</td>
                <td>
                  <Button variant="outline-primary" size="sm" onClick={() => handleOpenModal(tag)}>Editar</Button>{' '}
                  <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTag(tag.id)}>Excluir</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Modal para Adicionar/Editar Tag */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{currentTag ? 'Editar Tag' : 'Adicionar Nova Tag'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="tagName" className="mb-3">
            <Form.Label>Nome da Tag</Form.Label>
            <Form.Control
              type="text"
              name="nome"
              value={tagData.nome}
              onChange={handleInputChange}
              placeholder="Ex: Botox"
              autoFocus
            />
          </Form.Group>
          <Form.Group controlId="tagDescricao">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="descricao"
              value={tagData.descricao}
              onChange={handleInputChange}
              placeholder="Para que serve esta tag? Ex: Pacientes que já fizeram ou têm interesse em toxina botulínica."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveTag} disabled={isSaving}>
            {isSaving ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TagsInteresseTab;